import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    // Header
    privacy: 'Privacy',

    // Hero
    versionChip: 'Version 2.0 — Now with Full Chinese Support',
    subtitle: 'Bible Quiz · 圣经问答',
    scriptureVerse: '"Your word is a lamp to my feet and a light to my path."',
    scriptureRef: '— Psalm 119:105',
    heroBody:
      'A free Bible quiz and scripture study app for all ages — adults, parents, youth, and children. All 66 books of the Bible, in English and Simplified Chinese. Entirely offline. No account. No ads. Always free.',
    downloadBtn: 'Download on the App Store',

    // Screenshots
    screenshotsTitle: "Get Closer to the Lord's Actual Words",
    screenshotsBody:
      'Every question reveals a verse. Every feature exists to help you spend more time with scripture.',
    screenshots: [
      { caption: 'All 66 Books', sub: 'Genesis to Revelation' },
      { caption: 'Bible Knowledge', sub: 'Who, what, when, where' },
      { caption: 'Verse Memorization', sub: 'Fill-in-the-blank scripture' },
      { caption: 'Bible Context', sub: 'Passage-level understanding' },
      { caption: 'English · 中文', sub: 'BSB + 和合本 CUV' },
      { caption: 'Full Chinese Support', sub: 'Fully localized interface' },
      { caption: "Children's Mode", sub: 'Scripture-first, no gamification' },
    ],

    // Features
    featuresTitle: "Everything You Need, Nothing You Don't",
    featuresBody: "No ads. No subscriptions. No gamification. No internet required. Just God's Word.",
    features: [
      {
        title: 'Three Question Types',
        body: 'Bible Knowledge, Bible Context, and Verse Memorization — all rooted directly in scripture. No theology, no interpretation. What the Bible records.',
      },
      {
        title: 'Study Mode',
        body: 'Read a passage with one verse hidden and identify the missing text — learning each verse in context, not in isolation. Then go straight into fill-in-the-blank memorization.',
      },
      {
        title: 'Full Chinese Support (和合本)',
        body: 'Switch between English (Berean Standard Bible) and Simplified Chinese (和合本 CUV) at any time. All 66 books, fully localized — every label, verse reference, and book name.',
      },
      {
        title: "Children's Mode",
        body: "Easy questions, larger text, scripture-first content. No gamification, no leaderboards, no streaks. Just God's Word — for adults, parents, and children alike.",
      },
      {
        title: 'Entirely Offline & Free',
        body: "No internet required. No account. No ads. No in-app purchases. No data collection. Always free — because God's Word should be accessible to everyone.",
      },
    ],

    // CTA
    ctaTitle: 'Start Reading Scripture Today',
    ctaBody:
      'Free for everyone. Works offline. All 66 books in English and Chinese. No account needed.',
    ctaCaption: 'iOS only · iPhone & iPad · Free',

    // Footer
    copyright: `© ${new Date().getFullYear()} CuratedData LLC. All rights reserved.`,
    privacyPolicy: 'Privacy Policy',

    // Privacy Page
    privacyTitle: 'Privacy Policy',
    privacyLastUpdated: 'Last updated: April 29, 2026',
    privacyContent: [
      {
        heading: 'Introduction',
        body: 'Welcome to Biblitz. We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how Biblitz, operated by CuratedData LLC, handles your information.',
      },
      {
        heading: 'Information We Don\'t Collect',
        body: 'Biblitz is designed with your privacy in mind. The app does not collect any personal information, does not track your location, does not use analytics tools, does not store any user data on external servers, and does not require account creation. All quiz data and functionality is stored locally on your device.',
      },
      {
        heading: 'No Data Collection',
        body: 'Biblitz does not collect, store, transmit, or share any personal information or usage data from users. Your privacy is fully respected and protected. We have no servers, no databases, and no user accounts.',
      },
      {
        heading: 'Children\'s Privacy',
        body: 'Biblitz is safe for all ages, including children. Because we collect no personal information from any users, we are fully compliant with the Children\'s Online Privacy Protection Act (COPPA) and similar regulations worldwide.',
      },
      {
        heading: 'Local Storage Only',
        body: 'Your quiz performance, language preferences, and app settings are stored only on your device using iOS UserDefaults. This data never leaves your device and is only used to provide app functionality.',
      },
      {
        heading: 'No Third-Party Services',
        body: 'Biblitz does not integrate with any third-party analytics, advertising, or tracking services. The app works entirely offline and requires no internet connection.',
      },
      {
        heading: 'Changes to This Privacy Policy',
        body: 'We may update this privacy policy from time to time. The updated version will be indicated by the "Last Updated" date at the top of this policy.',
      },
      {
        heading: 'Contact Us',
        body: 'If you have any questions about this Privacy Policy, please contact us at',
      },
    ],

    // Terms Page
    termsTitle: 'Terms of Service',
    termsLastUpdated: 'Last updated: April 29, 2026',
    termsContent: [
      {
        heading: 'Acceptance of Terms',
        body: 'By downloading, installing, or using Biblitz, you agree to be bound by these Terms of Service. If you do not agree to these Terms, please do not use the app.',
      },
      {
        heading: 'About Biblitz',
        body: 'Biblitz is a Bible quiz and scripture study application developed and operated by CuratedData LLC. The app provides educational content based on the Holy Bible to help users learn and memorize scripture.',
      },
      {
        heading: 'License to Use',
        body: 'CuratedData LLC grants you a personal, non-exclusive, non-transferable, revocable license to use Biblitz for personal, non-commercial purposes. You may not modify, reverse engineer, extract content for commercial purposes, or use the app for any unlawful purpose.',
      },
      {
        heading: 'Content and Intellectual Property',
        body: 'Scripture texts: English (Berean Standard Bible - Public Domain) and Chinese (和合本 CUV - Public Domain). Quiz questions, app design, and code: © 2026 CuratedData LLC. All rights reserved. Questions created with AI assistance and human curation, based on public domain scripture texts.',
      },
      {
        heading: 'Disclaimer',
        body: 'While we strive for accuracy, questions are educational tools, not authoritative theological statements. Always refer to complete scripture passages and consult religious leaders for guidance. The app is provided "as is" without warranties of any kind.',
      },
      {
        heading: 'Limitation of Liability',
        body: 'To the maximum extent permitted by law, CuratedData LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the app. Our total liability shall not exceed $0 (as the app is free).',
      },
      {
        heading: 'Changes to Terms',
        body: 'We may modify these Terms at any time. Changes will be effective immediately upon posting. Your continued use of the app after changes constitutes acceptance of the modified Terms.',
      },
      {
        heading: 'Contact Information',
        body: 'For questions regarding these Terms, please contact CuratedData LLC at',
      },
    ],
  },

  zh: {
    // Header
    privacy: '隐私政策',

    // Hero
    versionChip: '2.0 版本 — 现已支持完整中文',
    subtitle: '圣经问答 · Bible Quiz',
    scriptureVerse: '"你的话是我脚前的灯，是我路上的光。"',
    scriptureRef: '— 诗篇 119:105',
    heroBody:
      '一款免费的圣经问答与经文学习应用，适合各年龄人士——成人、家长、青少年和儿童。涵盖圣经全部66卷书，提供英文与简体中文版本。完全离线使用，无需注册，无广告，永久免费。',
    downloadBtn: '在 App Store 下载',

    // Screenshots
    screenshotsTitle: '更亲近神话语的真义',
    screenshotsBody: '每道题目都揭示一节经文。每项功能都是为了帮助你更多时间与圣经同在。',
    screenshots: [
      { caption: '全部66卷书', sub: '从创世记到启示录' },
      { caption: '圣经知识', sub: '人物、事件、时间、地点' },
      { caption: '经文背诵', sub: '填空式经文练习' },
      { caption: '圣经背景', sub: '段落层面的理解' },
      { caption: '英文 · 中文', sub: 'BSB + 和合本 CUV' },
      { caption: '完整中文支持', sub: '全面本地化界面' },
      { caption: '儿童模式', sub: '以经文为核心，无游戏化设计' },
    ],

    // Features
    featuresTitle: '您所需的一切，仅此而已',
    featuresBody: '无广告。无订阅。无游戏化。无需联网。只有神的话语。',
    features: [
      {
        title: '三种题型',
        body: '圣经知识、圣经背景与经文背诵——全部直接源于圣经原文。无神学解释，无个人观点。只记录圣经所载之事。',
      },
      {
        title: '学习模式',
        body: '阅读一段经文，其中一节被隐藏，找出缺失的文字——在上下文中学习每节经文，而非孤立记忆。然后直接进入填空式背诵练习。',
      },
      {
        title: '完整中文支持（和合本）',
        body: '随时切换英文（Berean Standard Bible）与简体中文（和合本 CUV）。全部66卷书完整本地化——包括每个标签、经文引用与书卷名称。',
      },
      {
        title: '儿童模式',
        body: '简单题目、大号字体、以经文为核心的内容。无游戏化、无排行榜、无连续打卡。只有神的话语——适合成人、家长和孩子。',
      },
      {
        title: '完全离线与免费',
        body: '无需网络。无需账号。无广告。无内购。不收集数据。永久免费——因为神的话语应当人人皆可获取。',
      },
    ],

    // CTA
    ctaTitle: '今日开始阅读圣经',
    ctaBody: '人人免费。离线使用。全部66卷，英中双语。无需账号。',
    ctaCaption: '仅限 iOS · iPhone 和 iPad · 免费',

    // Footer
    copyright: `© ${new Date().getFullYear()} CuratedData LLC 版权所有。`,
    privacyPolicy: '隐私政策',

    // Privacy Page
    privacyTitle: '隐私政策',
    privacyLastUpdated: '最后更新：2026年4月29日',
    privacyContent: [
      {
        heading: '简介',
        body: '欢迎使用 Biblitz。我们尊重您的隐私并致力于保护您的个人数据。本隐私政策说明 Biblitz（由 CuratedData LLC 运营）如何处理您的信息。',
      },
      {
        heading: '我们不收集的信息',
        body: 'Biblitz 在设计时充分考虑了您的隐私。本应用不收集任何个人信息、不跟踪您的位置、不使用分析工具、不在外部服务器上存储任何用户数据，也不需要创建账号。所有问答数据和功能均存储在您的设备本地。',
      },
      {
        heading: '零数据收集',
        body: 'Biblitz 不收集、存储、传输或分享任何用户个人信息或使用数据。您的隐私受到充分尊重与保护。我们没有服务器、没有数据库、没有用户账号。',
      },
      {
        heading: '儿童隐私',
        body: 'Biblitz 适合各年龄人士使用，包括儿童。由于我们不收集任何用户的个人信息，我们完全符合《儿童在线隐私保护法》(COPPA) 及全球类似法规。',
      },
      {
        heading: '仅本地存储',
        body: '您的问答成绩、语言偏好和应用设置仅存储在您的设备上（使用 iOS UserDefaults）。这些数据永远不会离开您的设备，仅用于提供应用功能。',
      },
      {
        heading: '无第三方服务',
        body: 'Biblitz 不集成任何第三方分析、广告或跟踪服务。该应用完全离线运行，无需互联网连接。',
      },
      {
        heading: '本政策的变更',
        body: '我们可能会不时更新本隐私政策。更新版本将通过本政策顶部的"最后更新"日期标明。',
      },
      {
        heading: '联系我们',
        body: '如您对本隐私政策有任何疑问，请通过以下方式联系我们：',
      },
    ],

    // Terms Page
    termsTitle: '服务条款',
    termsLastUpdated: '最后更新：2026年4月29日',
    termsContent: [
      {
        heading: '接受条款',
        body: '下载、安装或使用 Biblitz 即表示您同意受本服务条款约束。如果您不同意这些条款，请不要使用本应用。',
      },
      {
        heading: '关于 Biblitz',
        body: 'Biblitz 是由 CuratedData LLC 开发和运营的圣经问答与经文学习应用程序。该应用提供基于圣经的教育内容，帮助用户学习和记忆经文。',
      },
      {
        heading: '使用许可',
        body: 'CuratedData LLC 授予您个人的、非独占的、不可转让的、可撤销的许可，以将 Biblitz 用于个人、非商业目的。您不得修改、逆向工程、提取内容用于商业目的，或将应用用于任何非法目的。',
      },
      {
        heading: '内容与知识产权',
        body: '经文文本：英文（Berean Standard Bible - 公有领域）和中文（和合本 CUV - 公有领域）。问答题目、应用设计和代码：© 2026 CuratedData LLC 版权所有。题目通过 AI 辅助和人工策划创建，基于公有领域的经文文本。',
      },
      {
        heading: '免责声明',
        body: '虽然我们力求准确，但题目是教育工具，而非权威神学声明。请始终参考完整的经文段落并咨询宗教领袖以获得指导。应用按"原样"提供，不提供任何形式的保证。',
      },
      {
        heading: '责任限制',
        body: '在法律允许的最大范围内，CuratedData LLC 对因您使用本应用而产生的任何间接、附带、特殊、后果性或惩罚性损害不承担责任。我们的总责任不超过 0 美元（因为本应用免费）。',
      },
      {
        heading: '条款变更',
        body: '我们可能随时修改这些条款。更改将在发布后立即生效。您在更改后继续使用应用即表示接受修改后的条款。',
      },
      {
        heading: '联系信息',
        body: '有关这些条款的问题，请通过以下方式联系 CuratedData LLC：',
      },
    ],
  },
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState('en');
  const t = translations[lang];
  const toggleLang = () => setLang((prev) => (prev === 'en' ? 'zh' : 'en'));
  return (
    <LanguageContext.Provider value={{ lang, t, toggleLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
